import React from 'react';
import { motion } from 'framer-motion';
import { Map, Calendar, Building2, Languages, Bell, Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';

const features = [
  {
    icon: Map,
    title: 'Smart Navigation',
    description: "Interactive maps and turn-by-turn navigation guide visitors through the city's attractions, F&B outlets, services and facilities.",
  },
  {
    icon: Calendar,
    title: 'Event Calendar',
    description: 'Stay updated with upcoming events and book tickets for attractions and experiences.',
  },
  {
    icon: Building2,
    title: 'Interactive City Guide',
    description: 'Comprehensive guide to attractions, F&B outlets, services and facilities with rich multimedia content.',
  },
  {
    icon: Languages,
    title: 'Multi-language Support',
    description: 'Experience the app in your preferred language with full localization support.',
  },
  {
    icon: Bell,
    title: 'Live Notifications',
    description: 'Real-time updates about events, offers, and city announcements.',
  },
  {
    icon: Sparkles,
    title: 'City Gems',
    description: 'Discover hidden spots, photo opportunities, and unique experiences throughout the city.',
  },
];

const ProjectVision = () => {
  return (
    <section className="py-24 bg-black">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-[1fr_400px] gap-12 lg:gap-24">
          {/* Left Column - Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-accent mb-4">The Solution</span>
            <h2 className="text-3xl md:text-4xl font-display font-semibold text-white mb-8">
              A Digital Gateway to Smart City Living
            </h2>
            <p className="text-lg text-gray-300 mb-12">
              We developed a comprehensive mobile solution that transforms how people interact with 
              Expo City Dubai, making every visit more engaging, informed, and memorable.
            </p>

            <div className="grid grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className={cn(
                      "group p-6 rounded-xl bg-white/5 border border-white/10",
                      "hover:bg-white/10 hover:border-accent/50 transition-all duration-300"
                    )}
                  >
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6 text-accent" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {feature.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right Column - App Screenshots */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative h-[700px]"
          >
            {/* Phone 1 - Main */}
            <motion.div
              initial={{ y: 0 }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute left-1/2 -translate-x-1/2 top-0 w-[280px] z-30"
            >
              <div className="relative aspect-[9/19.5]">
                <div className="absolute inset-0 rounded-[3rem] border-8 border-gray-800 bg-black overflow-hidden">
                  <img
                    src="/IMG_1043.PNG"
                    alt="Expo City Dubai App - Home Screen"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-6 bg-black rounded-b-2xl" />
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-800 rounded-full" />
              </div>
            </motion.div>

            {/* Phone 2 - Left Tilted */}
            <motion.div
             initial={{ y: -100 }} // Start 20px higher
              animate={{ y: [-100, -80, -100] }} // Adjust animation values
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut"}}
              className="absolute -left-08 top-1/2 -translate-y-1/2 w-[280px] z-20"
            >
              <div className="relative aspect-[9/19.5] transform -rotate-12">
                <div className="absolute inset-0 rounded-[3rem] border-8 border-gray-800 bg-black overflow-hidden">
                  <img
                    src="/IMG_1045.PNG"
                    alt="Expo City Dubai App - Events Screen"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-6 bg-black rounded-b-2xl" />
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-800 rounded-full" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProjectVision;